import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import GoogleMapsService from '../../../Services/GoogleMapsService';
import IconService from '../../../Services/IconService';
import PositionTooltip from '../PositionTooltip';
import { IRide, IRidePosition } from '../types';
import { IVehicle, IVehicleData } from '../../Vehicles/types';
import { activeRideNumberAtom, ignoreRideChangeAtom } from '../RidePlayerState';

interface IUseVehicleMarker {
  vehicleMarker: google.maps.Marker | null;
  removeVehicleMarker: () => void;
}

/**
 * useVehicleMarker custom hook
 * @param {google.maps.Map} map
 * @returns {IUseVehicleMarker}
 */
const useVehicleMarker = (
  map: google.maps.Map,
  // ride: IRide,
  rides: IRide[],
  isLastRidePlayer: boolean | undefined,
  vehicle: IVehicle | null,
  vehicleData: IVehicleData | null
): IUseVehicleMarker => {
  const intl = useIntl();
  const vehicleMarker = React.useRef<google.maps.Marker | null>(null);
  const vehicleMarkerTooltip = React.useRef<google.maps.OverlayView | null>(null);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);
  const ignoreRideChange = useRecoilValue<boolean>(ignoreRideChangeAtom);

  const handleToggleTooltip = React.useCallback((): void => {
    if (vehicleMarkerTooltip.current) {
      vehicleMarkerTooltip.current.setMap(vehicleMarkerTooltip.current.getMap() ? null : map);
    }
  }, [map]);

  const createVehicleTooltip = React.useCallback(
    (lastPosition: IRidePosition) => {
      let positionTooltip: JSX.Element | null = null;
      positionTooltip = (
        <PositionTooltip
          intl={intl}
          position={lastPosition}
          positionIndex={rides[activeRide - 1].positions.length - 1}
        />
      );

      const vehicleTooltip = GoogleMapsService.createRidePositionMarkerOverlay(
        rides[activeRide - 1].positions[rides[activeRide - 1].positions.length - 1],
        positionTooltip
      );
      vehicleMarkerTooltip.current = vehicleTooltip;
    },
    [activeRide, intl, rides]
  );

  // console.log(
  //   'positions.length - 1: ',
  //   rides[activeRide - 1].positions[rides[activeRide - 1].positions.length - 1]
  // );

  // Create or update vehicle marker
  React.useEffect(() => {
    if (isLastRidePlayer && vehicle && vehicleData && rides[activeRide - 1].positions) {
      const lastPosition =
        rides[activeRide - 1].positions[rides[activeRide - 1].positions.length - 1];
      const icon = {
        url: IconService.getVehicleIcon(
          vehicle.vehicleTypeId,
          vehicleData.status && vehicle.drivingMode === 'business'
            ? vehicleData.status
            : vehicle.drivingMode === 'private'
              ? 'PRIVATE'
              : 'UNKNOWN',
          lastPosition.gpsCourse,
          true,
          1,
          true
        ),
        scaledSize: new google.maps.Size(50, 50),
        anchor: new google.maps.Point(25, 25),
      };

      if (!vehicleMarker.current) {
        const position = new google.maps.LatLng(lastPosition.gpsLat, lastPosition.gpsLon);
        // const vehicleAngle = lastPosition.gpsCourse;
        vehicleMarker.current = new google.maps.Marker({ icon, position, map });
        vehicleMarker.current.setIcon(icon);
        vehicleMarker.current.setZIndex(Number(google.maps.Marker.MAX_ZINDEX) + 1);
        vehicleMarker.current.addListener('mouseover', () => {
          if (vehicleMarkerTooltip.current && vehicleMarkerTooltip.current.getMap()) {
            vehicleMarkerTooltip.current.setMap(null);
          }
          handleToggleTooltip();
        });
        vehicleMarker.current.addListener('mouseout', () => handleToggleTooltip());
        createVehicleTooltip(lastPosition);
      }
      if (vehicleMarker.current && !ignoreRideChange) {
        const position = new google.maps.LatLng(lastPosition.gpsLat, lastPosition.gpsLon);

        // Hide tooltip when vehicle position changes.
        const oldPosition = vehicleMarker.current.getPosition();
        if (
          oldPosition &&
          position.lat !== oldPosition.lat &&
          position.lng !== oldPosition.lng &&
          vehicleMarkerTooltip.current
        ) {
          vehicleMarkerTooltip.current.setMap(null);
        }

        vehicleMarker.current.setPosition(position);
        vehicleMarker.current.setIcon(icon);
        createVehicleTooltip(lastPosition);
      }
    }
  }, [
    activeRide,
    createVehicleTooltip,
    handleToggleTooltip,
    ignoreRideChange,
    isLastRidePlayer,
    map,
    rides,
    vehicle,
    vehicleData,
  ]);

  const removeVehicleMarker = () => {
    if (vehicleMarker.current) {
      vehicleMarker.current.setMap(null);
      vehicleMarker.current = null;
    }
    if (vehicleMarkerTooltip.current) {
      vehicleMarkerTooltip.current.setMap(null);
      vehicleMarkerTooltip.current = null;
    }
  };

  const data: IUseVehicleMarker = {
    vehicleMarker: vehicleMarker.current,
    removeVehicleMarker,
  };
  return data;
};

export default useVehicleMarker;
